import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  PlusOutlined,
  SnippetsOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import {
  Alert,
  App,
  Button,
  Empty,
  Popconfirm,
  Popover,
  Result,
  Spin,
  Switch,
  Tooltip,
} from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  assignSeat,
  deleteTable,
  exportSeating,
  getSeating,
  listTickets,
  reorderTables,
  setSeatingVisible,
} from '../api';
import SeatMap, { SEAT_COLOR, isSeatTaken, seatPersonName } from '../components/seat-map';
import SeatPicker, { SeatPickTarget } from '../components/seat-picker';
import TableModal, { TableModalMode } from '../components/table-modal';
import TablePasteModal from '../components/table-paste-modal';
import {
  AdminSeat,
  AdminSeatingView,
  AdminTable,
  GalaTicketRow,
  TABLE_SHAPE_META,
  errorText,
  moveId,
} from '../contract';
import { saveBlob } from '../lib/save-blob';
import { columnsForWidth } from '../lib/seat-layout';
import { tableNameMode } from '../lib/seat-names';
import { seatLabel } from '../lib/seat-text';
import styles from '../styles.module.scss';

interface Props {
  eventId: number;
}

/** Ghế trống nhấp nháy 2 lần (2 × 0,6 s) sau khi bấm một vé chưa có ghế. */
const FLASH_MS = 1200;

const takenOf = (t: AdminTable) => t.seats.filter(isSeatTaken).length;
const ticketName = (t: GalaTicketRow) => t.displayName || t.user?.nickName || `Vé #${t.id}`;
/** Ô tìm của bộ chọn: tên hiển thị của vé; vé chưa có tên (chỉ có UID) thì tìm theo UID. */
const pickerQuery = (t: GalaTicketRow) => (t.displayName?.trim() ? t.displayName : t.tiktokUid || ticketName(t));

/** Tab Chỗ ngồi: sơ đồ bàn ghế + bảng điều khiển (SPEC docs/gala/04 mục 7). */
const SeatingTab = ({ eventId }: Props) => {
  const { message } = App.useApp();
  const [view, setView] = useState<AdminSeatingView | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [columns, setColumns] = useState<2 | 3 | 4>(4);
  const wrapRef = useRef<HTMLDivElement>(null);

  const [picker, setPicker] = useState<SeatPickTarget | null>(null);
  const [move, setMove] = useState<{ seat: AdminSeat; table: AdminTable } | null>(null);
  const [tableModal, setTableModal] = useState<{ mode: TableModalMode; table?: AdminTable } | null>(
    null
  );
  const [pasteTable, setPasteTable] = useState<AdminTable | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [visibleSaving, setVisibleSaving] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Danh sách "vé chưa có ghế".
  const [unseatedOpen, setUnseatedOpen] = useState(false);
  const [unseated, setUnseated] = useState<GalaTicketRow[] | null>(null);
  const [hintTicket, setHintTicket] = useState<GalaTicketRow | null>(null);
  const [flash, setFlash] = useState(false);

  // Mỗi lần tải mang một số thứ tự: phản hồi cũ về muộn không được ghi đè phản hồi mới hơn.
  const loadSeq = useRef(0);
  const loaded = useRef(false);
  const load = useCallback(async () => {
    const mine = ++loadSeq.current;
    try {
      const v = await getSeating(eventId);
      if (mine !== loadSeq.current) return;
      loaded.current = true;
      setView(v);
      setLoadError(null);
    } catch (e) {
      if (mine !== loadSeq.current) return;
      // Đã có sơ đồ trên màn hình (tải lại sau một thay đổi): báo lỗi, giữ sơ đồ cũ.
      if (loaded.current) message.error(errorText(e, 'Không tải lại được sơ đồ.'));
      else setLoadError(errorText(e, 'Không tải được sơ đồ chỗ ngồi.'));
    }
  }, [eventId, message]);

  useEffect(() => {
    load();
  }, [load]);

  // Số cột lưới theo bề rộng khung sơ đồ (khung chỉ có sau khi tải xong lần đầu).
  const ready = view !== null;
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const apply = (w: number) => setColumns(columnsForWidth(w));
    apply(el.clientWidth);
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver((entries) =>
      apply(entries[0]?.contentRect.width ?? el.clientWidth)
    );
    ro.observe(el);
    return () => ro.disconnect();
  }, [ready]);

  // Đổi số cột thì ghế dời chỗ: đóng hộp chọn đang neo vào chỗ cũ.
  useEffect(() => setPicker(null), [columns]);

  // Esc huỷ chế độ chuyển ghế.
  useEffect(() => {
    if (!move) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMove(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [move]);

  useEffect(() => {
    if (!flash) return undefined;
    const timer = setTimeout(() => setFlash(false), FLASH_MS);
    return () => clearTimeout(timer);
  }, [flash]);

  const focusSeat = (seatId: number) => {
    const el = wrapRef.current?.querySelector<SVGGElement>(`[data-seat-id="${seatId}"]`);
    el?.focus();
  };

  /** Đóng hộp chọn; đóng bằng Esc / sau khi xếp thì trả tiêu điểm về ghế (dùng bàn phím liền mạch). */
  const closePicker = (refocus?: boolean) => {
    const id = picker?.seat.id;
    setPicker(null);
    if (refocus && id) focusSeat(id);
  };

  const moveTo = async (target: AdminSeat, table: AdminTable) => {
    if (!move) return;
    const { seat: from } = move;
    const name = seatPersonName(from) ?? '';
    setMove(null);
    // Tên tay chuyển bằng hai lần gọi (xếp ghế mới rồi bỏ ghế cũ): lần hai hỏng thì báo riêng.
    let placed = false;
    try {
      if (from.ticketId !== null) {
        await assignSeat(eventId, target.id, { ticketId: from.ticketId });
      } else {
        await assignSeat(eventId, target.id, { guestName: from.guestName });
        placed = true;
        await assignSeat(eventId, from.id, {});
      }
      message.success(`Đã chuyển ${name} sang ${seatLabel(table.name, target.number)}.`);
    } catch (e) {
      message.error(
        placed
          ? 'Đã xếp vào ghế mới nhưng chưa bỏ được ghế cũ, hãy bỏ tay.'
          : errorText(e, 'Không chuyển được ghế.')
      );
    }
    await load();
    focusSeat(target.id);
  };

  const onSeatClick = (seat: AdminSeat, table: AdminTable, anchor: DOMRect) => {
    if (move) {
      if (seat.id === move.seat.id) setMove(null);
      else if (isSeatTaken(seat)) message.warning('Ghế này đã có người — chọn một ghế trống.');
      else moveTo(seat, table);
      return;
    }
    const wrap = wrapRef.current?.getBoundingClientRect();
    if (!wrap) return;
    setPicker({
      seat,
      table,
      box: {
        left: anchor.left - wrap.left,
        top: anchor.top - wrap.top,
        width: anchor.width,
        height: anchor.height,
      },
    });
  };

  const startMove = (seat: AdminSeat, table: AdminTable) => {
    setPicker(null);
    setMove({ seat, table });
  };

  const run = async (id: number, task: () => Promise<unknown>, fail: string) => {
    setBusyId(id);
    try {
      await task();
      await load();
    } catch (e) {
      message.error(errorText(e, fail));
    } finally {
      setBusyId(null);
    }
  };

  const tables = useMemo(() => view?.tables ?? [], [view]);

  const moveTable = (table: AdminTable, delta: -1 | 1) =>
    run(
      table.id,
      () =>
        reorderTables(
          eventId,
          moveId(
            tables.map((t) => t.id),
            table.id,
            delta
          )
        ),
      'Không đổi được thứ tự bàn.'
    );

  const removeTable = (table: AdminTable) =>
    run(
      table.id,
      async () => {
        await deleteTable(eventId, table.id);
        message.success(`Đã xoá ${table.name}.`);
      },
      'Không xoá được bàn.'
    );

  const toggleVisible = async (visible: boolean) => {
    setVisibleSaving(true);
    try {
      await setSeatingVisible(eventId, visible);
      message.success(visible ? 'Khách đã xem được sơ đồ trong app.' : 'Đã ẩn sơ đồ khỏi app.');
      await load();
    } catch (e) {
      message.error(errorText(e, 'Không đổi được chế độ công bố.'));
    } finally {
      setVisibleSaving(false);
    }
  };

  const doExport = async () => {
    setExporting(true);
    try {
      const { blob, filename } = await exportSeating(eventId);
      saveBlob(blob, filename);
    } catch (e) {
      message.error(errorText(e, 'Không xuất được Excel.'));
    } finally {
      setExporting(false);
    }
  };

  const openUnseated = async (open: boolean) => {
    setUnseatedOpen(open);
    if (!open) return;
    setUnseated(null);
    try {
      const res = await listTickets(eventId, { seated: 0, page: 1, limit: 50 });
      setUnseated(res.data.filter((t) => t.status !== 'REVOKED'));
    } catch (e) {
      setUnseated([]);
      message.error(errorText(e, 'Không tải được danh sách vé.'));
    }
  };

  const pickUnseated = (t: GalaTicketRow) => {
    setUnseatedOpen(false);
    setHintTicket(t);
    setFlash(true);
    message.info(`Bấm một ghế trống trên sơ đồ để xếp ${ticketName(t)}.`);
  };

  if (!view) {
    return loadError ? (
      <Result status="warning" title={loadError} extra={<Button onClick={load}>Thử lại</Button>} />
    ) : (
      <div className={styles.seatLoading}>
        <Spin />
      </div>
    );
  }

  const { counts } = view;
  const moveName = move ? seatPersonName(move.seat) : null;

  const unseatedList = (
    <div className={styles.seatUnseatedList}>
      {unseated === null ? (
        <Spin size="small" />
      ) : unseated.length === 0 ? (
        <span className={styles.subtle}>Không còn vé nào chưa có ghế.</span>
      ) : (
        unseated.map((t) => (
          <button
            key={t.id}
            type="button"
            className={styles.seatPickRow}
            onClick={() => pickUnseated(t)}
          >
            <span className={styles.seatPickText}>
              <strong>{ticketName(t)}</strong>
              <span className={styles.subtle}>
                {t.tiktokUid ? `UID ${t.tiktokUid}` : t.user ? `@${t.user.username}` : 'chưa gắn'}
              </span>
            </span>
          </button>
        ))
      )}
    </div>
  );

  return (
    <div className={styles.seatLayout}>
      <div className={styles.seatMapCol}>
        <div className={styles.seatSvgWrap} ref={wrapRef}>
          {/* Nổi đè lên dải sân khấu để sơ đồ không bị đẩy xuống (ghế đích không đổi chỗ). */}
          {move && (
            <Alert
              className={styles.seatMoveBar}
              type="info"
              showIcon
              message={`Đang chuyển ${moveName} (${seatLabel(
                move.table.name,
                move.seat.number
              )}) — bấm một ghế trống, Esc để huỷ.`}
              action={
                <Button size="small" onClick={() => setMove(null)}>
                  Huỷ
                </Button>
              }
            />
          )}
          <SeatMap
            view={view}
            columns={columns}
            selectedSeatId={move?.seat.id ?? picker?.seat.id ?? null}
            moveMode={!!move}
            flashEmpty={flash}
            onSeatClick={onSeatClick}
          />
          <SeatPicker
            eventId={eventId}
            target={picker}
            initialQuery={hintTicket ? pickerQuery(hintTicket) : undefined}
            onClose={closePicker}
            onChanged={async () => {
              setHintTicket(null);
              await load();
            }}
            onStartMove={startMove}
          />
        </div>
        <div className={styles.seatLegend}>
          <LegendDot
            fill={SEAT_COLOR.emptyFill}
            stroke={SEAT_COLOR.emptyStroke}
            label="Ghế trống"
          />
          <LegendDot fill={SEAT_COLOR.ticket} stroke={SEAT_COLOR.ticket} label="Có vé" />
          <LegendDot fill={SEAT_COLOR.guest} stroke={SEAT_COLOR.guest} label="Tên ghi tay" />
        </div>
      </div>

      <div className={styles.seatPanel}>
        <div className={styles.card}>
          <p className={styles.seatCounts}>
            Đã xếp {counts.ticketsSeated}/{counts.ticketsActive} vé · {counts.taken}/{counts.seats}{' '}
            ghế
          </p>
          {counts.ticketsUnseated > 0 && (
            <Popover
              open={unseatedOpen}
              onOpenChange={openUnseated}
              trigger="click"
              placement="bottomLeft"
              title="Vé chưa có ghế"
              content={unseatedList}
            >
              <Button size="small">Xem {counts.ticketsUnseated} vé chưa có ghế</Button>
            </Popover>
          )}
          {hintTicket && (
            <p className={styles.hint}>
              Bấm một ghế trống trên sơ đồ để xếp <strong>{ticketName(hintTicket)}</strong>.{' '}
              <Button type="link" size="small" onClick={() => setHintTicket(null)}>
                Bỏ chọn
              </Button>
            </p>
          )}

          <div className={styles.seatSwitchRow}>
            <Switch
              checked={view.visible}
              loading={visibleSaving}
              onChange={toggleVisible}
              id="gala-seat-visible"
            />
            <label htmlFor="gala-seat-visible">Cho khách xem sơ đồ</label>
          </div>
          <p className={styles.hint}>Tắt thì app không hiện chỗ ngồi; quầy quét vẫn thấy.</p>

          <div className={styles.seatActions}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setTableModal({ mode: 'create' })}
            >
              Thêm bàn
            </Button>
            <Button icon={<ThunderboltOutlined />} onClick={() => setTableModal({ mode: 'bulk' })}>
              Thêm nhanh
            </Button>
            <Button icon={<DownloadOutlined />} loading={exporting} onClick={doExport}>
              Xuất Excel
            </Button>
          </div>
        </div>

        {tables.length === 0 ? (
          <Empty description="Chưa có bàn nào." />
        ) : (
          <div className={styles.seatTableList}>
            {tables.map((table, index) => {
              const taken = takenOf(table);
              return (
                <div key={table.id} className={styles.seatTableCard}>
                  <div className={styles.seatTableHead}>
                    <strong className={styles.seatTableName} title={table.name}>
                      {table.name}
                    </strong>
                    <span className={styles.subtle}>
                      {TABLE_SHAPE_META[table.shape]?.text ?? table.shape} · {taken}/
                      {table.capacity}
                    </span>
                    {taken > 0 ? (
                      <Tooltip title="Bỏ hết ghế trước">
                        <Button
                          size="small"
                          danger
                          icon={<DeleteOutlined />}
                          aria-label={`Xoá ${table.name}`}
                          disabled
                        />
                      </Tooltip>
                    ) : (
                      <Popconfirm
                        title={`Xoá ${table.name}?`}
                        description="Xoá bàn và các ghế trống của bàn."
                        okText="Xoá"
                        okButtonProps={{ danger: true }}
                        cancelText="Không"
                        onConfirm={() => removeTable(table)}
                      >
                        <Button
                          size="small"
                          danger
                          icon={<DeleteOutlined />}
                          aria-label={`Xoá ${table.name}`}
                          loading={busyId === table.id}
                          disabled={busyId !== null && busyId !== table.id}
                        />
                      </Popconfirm>
                    )}
                  </div>
                  <div className={styles.seatTableTools}>
                    <Button
                      size="small"
                      icon={<ArrowUpOutlined />}
                      aria-label={`Dời ${table.name} lên`}
                      disabled={index === 0 || busyId !== null}
                      onClick={() => moveTable(table, -1)}
                    />
                    <Button
                      size="small"
                      icon={<ArrowDownOutlined />}
                      aria-label={`Dời ${table.name} xuống`}
                      disabled={index === tables.length - 1 || busyId !== null}
                      onClick={() => moveTable(table, 1)}
                    />
                    <Button
                      size="small"
                      icon={<EditOutlined />}
                      onClick={() => setTableModal({ mode: 'edit', table })}
                    >
                      Sửa
                    </Button>
                    <Button
                      size="small"
                      icon={<SnippetsOutlined />}
                      onClick={() => setPasteTable(table)}
                    >
                      Dán danh sách
                    </Button>
                  </div>
                  {/* Bàn quá đông thì sơ đồ không in tên: liệt kê ở đây để vẫn đọc được ai ngồi đâu. */}
                  {taken > 0 && tableNameMode(table.shape, table.capacity) === 'hidden' && (
                    <p className={styles.seatTableNames}>
                      {table.seats
                        .filter(isSeatTaken)
                        .map((s) => `Ghế ${s.number} ${seatPersonName(s)}`)
                        .join(' · ')}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <TableModal
        eventId={eventId}
        mode={tableModal?.mode ?? null}
        table={tableModal?.table ?? null}
        onClose={() => setTableModal(null)}
        onSaved={load}
      />
      <TablePasteModal
        eventId={eventId}
        table={pasteTable}
        onClose={() => setPasteTable(null)}
        onDone={load}
      />
    </div>
  );
};

const LegendDot = ({ fill, stroke, label }: { fill: string; stroke: string; label: string }) => (
  <span className={styles.seatLegendItem}>
    <svg width={14} height={14} aria-hidden>
      <circle cx={7} cy={7} r={6} fill={fill} stroke={stroke} strokeWidth={1.5} />
    </svg>
    {label}
  </span>
);

export default SeatingTab;
